import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Download, FileBarChart2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const downloadBlobResponse = (res, fallbackName) => {
  const cd = res.headers?.['content-disposition'] || '';
  const m = cd.match(/filename="?([^"]+)"?/);
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url; a.download = (m && m[1]) || fallbackName; a.click();
  URL.revokeObjectURL(url);
};

/** On-screen consolidated feedback report (same HTML as the PDF) with a Download PDF action. */
export const FeedbackReportDialog = ({ placementId, open, onOpenChange }) => {
  const [meta, setMeta] = useState(null);
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open || !placementId) return;
    setLoading(true); setHtml(''); setMeta(null);
    Promise.all([
      api.get(`/feedback-reports/placement/${placementId}`),
      api.get(`/feedback-reports/placement/${placementId}/html`, { responseType: 'text' }),
    ]).then(([m, h]) => { setMeta(m.data); setHtml(h.data); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to build report'))
      .finally(() => setLoading(false));
  }, [open, placementId]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/feedback-reports/placement/${placementId}/pdf`, { responseType: 'blob' });
      downloadBlobResponse(res, 'Consolidated_Feedback.pdf');
    } catch (e) {
      toast.error('PDF download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0 gap-0" data-testid="feedback-report-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="font-heading text-xl text-ica-navy flex items-center gap-2">
                <FileBarChart2 className="w-5 h-5 text-ica-gold" /> Consolidated Feedback
                {meta && (
                  <span data-testid="feedback-report-badge" className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${meta.interim ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-700'}`}>
                    {meta.interim ? 'Interim' : 'Final'}
                  </span>
                )}
              </DialogTitle>
              <DialogDescription data-testid="feedback-report-subtitle">
                {meta ? `${meta.student.name} · ${meta.restaurant.name} · ${meta.assessments_submitted} of ${meta.assessments_total} assessments submitted` : 'Building report…'}
              </DialogDescription>
            </div>
            <Button data-testid="feedback-report-download" size="sm" onClick={downloadPdf} disabled={downloading || loading} className="shrink-0">
              <Download className="w-4 h-4 mr-2" /> {downloading ? 'Preparing…' : 'Download PDF'}
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 bg-gray-100">
          {loading ? (
            <p className="text-sm text-gray-500 text-center py-10">Building report…</p>
          ) : (
            <iframe title="Consolidated feedback report" data-testid="feedback-report-frame" srcDoc={html} className="w-full h-full bg-white" sandbox="" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackReportDialog;
